import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createConnection,
  createFacebookConnection,
  deleteConnection,
  facebookCallback,
  fetchBoatPosts,
  fetchConnections,
  fetchIntegrationPosts,
  postBoatManually,
  startFacebookConnect,
  updateConnection,
} from './api'
import type { ConnectionWriteBody, ManualPostBody } from '@/types/integrations'

export const CONNECTIONS_KEY = ['integrations-connections'] as const
export const INTEGRATION_POSTS_KEY = ['integration-posts'] as const

export const useConnections = () =>
  useQuery({ queryKey: CONNECTIONS_KEY, queryFn: fetchConnections })

export const useCreateConnection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createConnection,
    onSuccess: () => qc.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  })
}

export const useUpdateConnection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ConnectionWriteBody }) =>
      updateConnection(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  })
}

export const useDeleteConnection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteConnection,
    onSuccess: () => qc.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  })
}

export const useStartFacebookConnect = () => useMutation({ mutationFn: startFacebookConnect })

export const useFacebookCallback = () =>
  useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) => facebookCallback(code, state),
  })

export const useCreateFacebookConnection = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createFacebookConnection,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONNECTIONS_KEY })
      qc.invalidateQueries({ queryKey: INTEGRATION_POSTS_KEY })
    },
  })
}

export const useIntegrationPosts = () =>
  useQuery({ queryKey: INTEGRATION_POSTS_KEY, queryFn: fetchIntegrationPosts })

export const useBoatPosts = (boatId: string) =>
  useQuery({
    queryKey: [...INTEGRATION_POSTS_KEY, 'boat', boatId],
    queryFn: () => fetchBoatPosts(boatId),
    enabled: !!boatId,
  })

export const usePostBoatManually = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ boatId, body }: { boatId: string; body: ManualPostBody }) =>
      postBoatManually(boatId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INTEGRATION_POSTS_KEY })
    },
  })
}
